import { LANE_COUNT, TILE } from './constants';
import { moveBodyX, moveBodyY, overlapsSolid, type Body } from './physics';
import { T_EMPTY, tileAt, type EnemyKind, type Room, type Spawn } from './room';
import type { Player, SimEvent } from './simulation';
import { RANGER_PHASES, TUNING as T, type RangerPhase } from './tuning';

/**
 * Enemy behaviour, one small state machine per kind (GDD: Enemies and bosses). Crawlers are
 * wildlife, drones are the captors' machines, and the Ranger is the first rogue-suit boss: its
 * signature is the lane-hopping ambush, so it is only ever hittable in the player's own lane.
 */

export type EnemyMode =
  // Crawler.
  | 'walk'
  // Drone: drifting, then glowing before a shot.
  | 'hover'
  | 'aim'
  // Ranger.
  | 'intro'
  | 'stalk'
  | 'tell'
  | 'windup'
  | 'charge'
  | 'recover';

export interface Enemy extends Body {
  readonly id: number;
  readonly kind: EnemyKind;
  lane: number;
  dir: 1 | -1;
  hp: number;
  readonly maxHp: number;
  hitFlash: number;
  alive: boolean;
  mode: EnemyMode;
  /** Ticks spent in the current mode. */
  timer: number;
  /** Where it was placed: drones hover around it. */
  readonly homeX: number;
  readonly homeY: number;
  /** Charges left in the Ranger's current ambush. */
  charges: number;
}

/** A hostile shot: hurts the player in its lane, stopped by solid tiles. */
export interface Bolt {
  readonly id: number;
  readonly lane: number;
  x: number;
  y: number;
  readonly vx: number;
  readonly vy: number;
  life: number;
}

/** What an enemy can see of the world and do to it. */
export interface EnemyWorld {
  readonly room: Room;
  readonly player: Player;
  readonly tick: number;
  emit(event: SimEvent): void;
  fireBolt(from: Enemy, vx: number, vy: number): void;
}

const SIZE: Readonly<Record<EnemyKind, { w: number; h: number }>> = {
  crawler: { w: 14, h: 10 },
  drone: { w: 12, h: 12 },
  ranger: { w: 10, h: 22 },
};

const HP: Readonly<Record<EnemyKind, number>> = {
  crawler: T.crawlerHp,
  drone: T.droneHp,
  ranger: T.rangerHp,
};

const FIRST_MODE: Readonly<Record<EnemyKind, EnemyMode>> = {
  crawler: 'walk',
  drone: 'hover',
  ranger: 'intro',
};

export function spawnEnemy(id: number, s: Spawn): Enemy {
  const { w, h } = SIZE[s.kind];
  const x = s.tx * TILE + (TILE - w) / 2;
  // Walkers stand on the floor of their tile; drones float in the middle of it.
  const y = s.kind === 'drone' ? s.ty * TILE + (TILE - h) / 2 : (s.ty + 1) * TILE - h;
  return {
    id,
    kind: s.kind,
    lane: s.lane,
    x,
    y,
    w,
    h,
    vx: 0,
    vy: 0,
    dir: -1,
    hp: HP[s.kind],
    maxHp: HP[s.kind],
    hitFlash: 0,
    alive: true,
    mode: FIRST_MODE[s.kind],
    timer: 0,
    homeX: x,
    homeY: y,
    charges: 0,
  };
}

export function updateEnemy(e: Enemy, w: EnemyWorld): void {
  if (e.hitFlash > 0) e.hitFlash--;
  e.timer++;
  if (e.kind === 'crawler') updateCrawler(e, w);
  else if (e.kind === 'drone') updateDrone(e, w);
  else updateRanger(e, w);
  if (e.y > w.room.height + TILE * 2) {
    // A boss is never lost down a pit (its defeat pays out and opens the doors): back home.
    if (e.kind === 'ranger') {
      e.x = e.homeX;
      e.y = e.homeY;
      e.vy = 0;
    } else {
      e.alive = false;
    }
  }
}

/** The Ranger's phase (0–2) from its health: thirds of the bar. */
export function rangerPhaseIndex(e: Enemy): 0 | 1 | 2 {
  if (e.hp > (e.maxHp * 2) / 3) return 0;
  if (e.hp > e.maxHp / 3) return 1;
  return 2;
}

function setMode(e: Enemy, mode: EnemyMode): void {
  e.mode = mode;
  e.timer = 0;
}

/** From the enemy's centre to the player's. */
function toPlayer(e: Enemy, p: Player): { dx: number; dy: number; dist: number } {
  const dx = p.x + p.w / 2 - (e.x + e.w / 2);
  const dy = p.y + p.h / 2 - (e.y + e.h / 2);
  return { dx, dy, dist: Math.hypot(dx, dy) };
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

/** Speeds up a falling enemy by the room's gravity. */
function pull(e: Enemy, room: Room): void {
  e.vy = Math.min(e.vy + T.gravity * room.gravity, T.maxFall * room.gravity);
}

/** Gravity and the vertical move for things that walk. */
function fall(e: Enemy, room: Room): void {
  pull(e, room);
  if (moveBodyY(room, e.lane, e, e.vy) !== 'none') e.vy = 0;
}

function updateCrawler(e: Enemy, w: EnemyWorld): void {
  e.vx = e.dir * T.crawlerSpeed;
  if (moveBodyX(w.room, e.lane, e, e.vx)) e.dir = e.dir === 1 ? -1 : 1;
  pull(e, w.room);
  const hit = moveBodyY(w.room, e.lane, e, e.vy);
  if (hit !== 'none') e.vy = 0;
  // Turn at ledges instead of walking off.
  if (hit === 'floor') {
    const footX = e.dir === 1 ? e.x + e.w + 1 : e.x - 1;
    const below = tileAt(
      w.room,
      e.lane,
      Math.floor(footX / TILE),
      Math.floor((e.y + e.h + 1) / TILE),
    );
    if (below === T_EMPTY) e.dir = e.dir === 1 ? -1 : 1;
  }
}

/**
 * Drones bob around home and drift to a spot beside the player (on a leash), then hold still and
 * glow for a moment before firing one bolt where the player is.
 */
function updateDrone(e: Enemy, w: EnemyWorld): void {
  const p = w.player;
  const { dx, dy, dist } = toPlayer(e, p);
  const sees = p.lane === e.lane && dist < T.droneSight;
  if (dx !== 0) e.dir = dx > 0 ? 1 : -1;

  if (e.mode === 'aim') {
    e.vx = 0;
    e.vy = 0;
    if (e.timer >= T.droneAimTicks) {
      if (p.lane === e.lane) {
        const len = dist || 1;
        w.fireBolt(e, (dx / len) * T.boltSpeed, (dy / len) * T.boltSpeed);
      }
      setMode(e, 'hover');
    }
    return;
  }

  let goalX = e.homeX;
  if (sees) {
    const beside = p.x + p.w / 2 - e.w / 2 - Math.sign(dx) * T.droneKeepAway;
    goalX = clamp(beside, e.homeX - T.droneLeash, e.homeX + T.droneLeash);
  }
  const goalY = e.homeY + Math.sin((w.tick + e.id * 29) / 24) * 6;
  e.vx = clamp((goalX - e.x) * 0.04, -T.droneSpeed, T.droneSpeed);
  e.vy = clamp((goalY - e.y) * 0.08, -T.droneSpeed, T.droneSpeed);
  moveBodyX(w.room, e.lane, e, e.vx);
  moveBodyY(w.room, e.lane, e, e.vy, false);

  if (sees && e.timer >= T.droneFireEvery) {
    setMode(e, 'aim');
    w.emit({ type: 'droneAim', id: e.id });
  }
}

/**
 * The Ranger: stalks level with the player from another lane, flashes (the tell), hops in behind
 * the player and charges along the floor, then stands dazed (the opening) before hopping away.
 */
function updateRanger(e: Enemy, w: EnemyWorld): void {
  const p = w.player;
  const phase = RANGER_PHASES[rangerPhaseIndex(e)];
  const { dx } = toPlayer(e, p);
  const facePlayer = () => {
    if (dx !== 0) e.dir = dx > 0 ? 1 : -1;
  };
  let vx = 0;
  switch (e.mode) {
    case 'intro':
      facePlayer();
      if (e.timer >= T.rangerIntroTicks) hopAway(e, w);
      break;
    case 'stalk':
      facePlayer();
      if (Math.abs(dx) > 8) vx = e.dir * T.rangerRun;
      // Following it into its lane doesn't catch it off guard for long.
      if (e.timer >= phase.stalkTicks || (e.lane === p.lane && e.timer >= 30)) {
        setMode(e, 'tell');
        w.emit({ type: 'bossTell' });
      }
      break;
    case 'tell':
      facePlayer();
      if (e.timer >= phase.tellTicks) ambush(e, w, phase);
      break;
    case 'windup':
      facePlayer();
      if (e.timer >= T.rangerWindupTicks) {
        setMode(e, 'charge');
        w.emit({ type: 'bossCharge' });
      }
      break;
    case 'charge':
      vx = e.dir * phase.chargeSpeed;
      break;
    case 'recover':
      if (e.timer >= phase.recoverTicks) hopAway(e, w);
      break;
    default:
      break;
  }

  e.vx = vx;
  const hitWall = moveBodyX(w.room, e.lane, e, e.vx);
  fall(e, w.room);
  if (e.mode === 'charge' && (hitWall || e.timer >= T.rangerChargeMaxTicks)) {
    if (hitWall) w.emit({ type: 'bossSlam' });
    e.charges--;
    if (e.charges > 0) {
      setMode(e, 'windup');
    } else {
      setMode(e, 'recover');
      volley(e, w, phase.volley);
    }
  }
}

/** Hops into the player's lane just behind them (or in front, if behind is a wall). */
function ambush(e: Enemy, w: EnemyWorld, phase: RangerPhase): void {
  const p = w.player;
  if (e.lane !== p.lane) {
    const y = p.y + p.h - e.h;
    const behind = -p.facing;
    const spot = [behind, -behind]
      .map((side) => p.x + p.w / 2 + side * T.rangerAmbushGap - e.w / 2)
      .find(
        (x) => x >= 0 && x + e.w <= w.room.width && !overlapsSolid(w.room, p.lane, { ...e, x, y }),
      );
    if (spot === undefined) {
      setMode(e, 'stalk');
      return;
    }
    hop(e, w, p.lane, spot, y);
  }
  e.charges = phase.charges;
  setMode(e, 'windup');
}

/** Leaves for the lane farthest from the player, where it can't be shot. */
function hopAway(e: Enemy, w: EnemyWorld): void {
  const p = w.player;
  const lanes = Array.from({ length: LANE_COUNT }, (_, i) => i)
    .filter((lane) => lane !== p.lane && !overlapsSolid(w.room, lane, e))
    .sort((a, b) => Math.abs(b - p.lane) - Math.abs(a - p.lane));
  const to = lanes[0];
  if (to === undefined) {
    // Walled in everywhere else: try again shortly.
    e.timer = Math.max(0, e.timer - 30);
    return;
  }
  if (to !== e.lane) hop(e, w, to, e.x, e.y);
  setMode(e, 'stalk');
}

/** Moves to another lane (and spot), telling the renderer where it left from and landed. */
function hop(e: Enemy, w: EnemyWorld, to: number, x: number, y: number): void {
  w.emit({
    type: 'bossHop',
    id: e.id,
    from: e.lane,
    to,
    fromX: e.x + e.w / 2,
    fromY: e.y + e.h / 2,
    toX: x + e.w / 2,
    toY: y + e.h / 2,
  });
  e.lane = to;
  e.x = x;
  e.y = y;
  e.vy = 0;
}

/** A fan of bolts at the player, if they are in its lane. */
function volley(e: Enemy, w: EnemyWorld, count: number): void {
  const p = w.player;
  if (count === 0 || p.lane !== e.lane) return;
  const { dx, dy } = toPlayer(e, p);
  const aim = Math.atan2(dy, dx);
  for (let i = 0; i < count; i++) {
    const a = aim + (i - (count - 1) / 2) * T.rangerSpread;
    w.fireBolt(e, Math.cos(a) * T.boltSpeed, Math.sin(a) * T.boltSpeed);
  }
}
